import {createSlice} from '@reduxjs/toolkit';

import ModalTypes from '@/consts/app/modalTypes';

const initialState = {
    globalModals: [],
    renderedModals: [],
};

const DEFAULT_MODAL_ORDER = 1000;
const HIGHEST_MODAL_ORDER = 99999;

export const slice = createSlice({
    name: 'modal',
    initialState,
    reducers: {
        showModalAction: (state, action) => {
            const {modalType, modalProps, order = DEFAULT_MODAL_ORDER, alwaysDisplay = false} = action.payload;

            if (state.globalModals.some((modal) => modal.modalType === modalType)) {
                return state;
            }

            state.globalModals.push({
                modalType,
                modalProps,
                order,
                alwaysDisplay,
            });

            return state;
        },
        hideModalAction: (state, action) => {
            const modalType = action.payload;

            state.globalModals = state.globalModals.filter((modal) => modal.modalType !== modalType);

            return state;
        },
        hideAllModalsAction: (state, action) => {
            return {...state, globalModals: [], renderedModals: []};
        },
        addModalToRenderedModals: (state, action) => {
            const id = action.payload;

            if (state.renderedModals.some((modalId) => modalId === id)) {
                return state;
            }

            state.renderedModals.push(id);

            return state;
        },
        removeModalFromRenderedModals: (state, action) => {
            const id = action.payload;

            state.renderedModals = state.renderedModals.filter((modalId) => modalId !== id);

            return state;
        },
    },
});

export const {
    showModalAction,
    hideModalAction,
    hideAllModalsAction,
    addModalToRenderedModals,
    removeModalFromRenderedModals,
} = slice.actions;

export const showLeviaLearnMoreModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.LEVIA_LEAR_MORE, modalProps, order: HIGHEST_MODAL_ORDER});

export const showFirmwareAvailableModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.FIRMWARE_AVAILABLE, modalProps, order: HIGHEST_MODAL_ORDER});

export const showBrowserNotCompatibleModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.BROWSER_NOT_COMPATIBLE, modalProps, order: HIGHEST_MODAL_ORDER});

export const showConsumerFeedbackGatheringModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.CONSUMER_FEEDBACK_GATHERING, modalProps, order: HIGHEST_MODAL_ORDER});

export const showLanguageSelectModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.LANGUAGE_SELECT, modalProps});

export const showMaintenancePopup = (modalProps) =>
    showModalAction({modalType: ModalTypes.MAINTENANCE, modalProps, order: HIGHEST_MODAL_ORDER});

export const showEnableNotificationModal = (modalProps) =>
    showModalAction({modalType: ModalTypes.ENABLE_NOTIFICATION, modalProps, order: HIGHEST_MODAL_ORDER});

export const showEnableNotificationReminderModal = (modalProps) =>
    showModalAction({modalType: ModalTypes.ENABLE_NOTIFICATION_REMINDER, modalProps, order: HIGHEST_MODAL_ORDER});

export const showResetNotificationPermissionsModal = (modalProps) =>
    showModalAction({modalType: ModalTypes.RESET_NOTIFICATION_PERMISSIONS, modalProps, order: HIGHEST_MODAL_ORDER});

export const showAutoDeliveryStatusNoneDetailsPopup = (modalProps) =>
    showModalAction({modalType: ModalTypes.AUTO_DELIVERY_STATUS_NONE_DETAILS, modalProps, order: DEFAULT_MODAL_ORDER});

export const showAutoDeliveryStatusInactiveDetailsPopup = (modalProps) =>
    showModalAction({
        modalType: ModalTypes.AUTO_DELIVERY_STATUS_INACTIVE_DETAILS,
        modalProps,
        order: DEFAULT_MODAL_ORDER,
    });

export const showQuickOrderPopup = (modalProps) =>
    showModalAction({
        modalType: ModalTypes.QUICK_ORDER,
        modalProps,
        order: DEFAULT_MODAL_ORDER,
    });

export const showConnectionSelectionModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.CONNECTION_SELECTION, modalProps, order: DEFAULT_MODAL_ORDER});

export const showConnectionCancellationModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.CONNECTION_CANCELLATION, modalProps, order: DEFAULT_MODAL_ORDER});

export const showHolderRegistrationModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.HOLDER_REGISTRATION, modalProps, order: DEFAULT_MODAL_ORDER});

export const showDeviceRegistrationErrorModalAction = (modalProps) =>
    showModalAction({modalType: ModalTypes.DEVICE_REGISTRATION_ERROR, modalProps, order: DEFAULT_MODAL_ORDER});

export const hideAutoDeliveryStatusNoneDetailsPopup = () =>
    hideModalAction(ModalTypes.AUTO_DELIVERY_STATUS_NONE_DETAILS);

export const hideAutoDeliveryStatusInactiveDetailsPopup = () =>
    hideModalAction(ModalTypes.AUTO_DELIVERY_STATUS_INACTIVE_DETAILS);

export const showEarnBadgePopup = (modalProps) =>
    showModalAction({modalType: ModalTypes.EARN_BADGE, modalProps, order: DEFAULT_MODAL_ORDER});

export const showEarnJourneyLevelTaskPopup = (modalProps) =>
    showModalAction({modalType: ModalTypes.COMPLETE_JOURNEY_LEVEL_TASK, modalProps, order: DEFAULT_MODAL_ORDER});

export const showNextJourneyLevelTaskPopup = (modalProps) =>
    showModalAction({modalType: ModalTypes.NEXT_JOURNEY_LEVEL_TASK, modalProps, order: DEFAULT_MODAL_ORDER});

export const showNewBadgePopup = (modalProps) =>
    showModalAction({modalType: ModalTypes.NEW_BADGE, modalProps, order: DEFAULT_MODAL_ORDER});

export const showExpiringBadgePopup = (modalProps) =>
    showModalAction({modalType: ModalTypes.EXPIRING_BADGE, modalProps, order: DEFAULT_MODAL_ORDER});

export const hideNewBadgePopup = () => hideModalAction(ModalTypes.NEW_BADGE);

export const hideEarnBadgePopup = () => hideModalAction(ModalTypes.EARN_BADGE);

export const hideQuickOrderPopup = () => hideModalAction(ModalTypes.QUICK_ORDER);

export const showAgeDeniedPopup = (modalProps) =>
    showModalAction({modalType: ModalTypes.AGE_GATE_DENIED, modalProps, order: DEFAULT_MODAL_ORDER});

export const hideAgeDeniedPopup = () => hideModalAction(ModalTypes.AGE_GATE_DENIED);

export const hideCompleteJourneyLevelTaskPopup = () => hideModalAction(ModalTypes.COMPLETE_JOURNEY_LEVEL_TASK);

export const hideNextJourneyLevelTaskPopup = () => hideModalAction(ModalTypes.NEXT_JOURNEY_LEVEL_TASK);

export const hideExpiringBadgePopup = () => hideModalAction(ModalTypes.EXPIRING_BADGE);

export default slice.reducer;
